// @ts-strict-ignore
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { View, ScrollView, TouchableHighlight } from 'react-native'
import Icon from 'react-native-vector-icons/Ionicons'
import styled, { useTheme } from 'styled-components/native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import { selectHistoryState } from 'APP/Store/History/selectors'

// Utils
import { useOnLayout, useScrollToTop } from 'APP/Hooks/Layout'
import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'
import { isMobile, isWeb } from 'APP/Helpers/checkPlatform'

// Styles
import { Colors } from 'APP/Themes'
import { Styles } from './style'

// Components
import AddFamilyMemberModal from 'APP/Components/AddFamilyMemberModal'
import Button from 'APP/Converse/Button'
import IconCard from 'APP/Converse/IconCard'
import DropDown from 'APP/Converse/Inputs/DropDown'
import Typography from 'APP/Converse/Typography'

import { Choice } from './types'

const SkipButton = styled(Button)`
  margin-top: ${(props) => props.theme.metrics.baseMargin / 2}px;
`

const ListCardItemsContainer = styled.View`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-top: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
`

const StickyButtonContainer = styled.View`
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-top: ${({ theme }) => theme.metrics.baseMargin}px;
  position: ${() => (isMobile() ? 'fixed' : 'relative')};
  padding-bottom: ${({ theme }) => theme.insets.bottom}px;
`

const SingleChoiceFullListContainer = styled.View`
  height: ${({ theme }) =>
    isMobile() ? 0.6 * theme.metrics.screenHeight : 0.7 * theme.metrics.screenHeight}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  width: ${({ theme }) => (isMobile() ? `${theme.metrics.screenWidth}px` : 'auto')};
  padding-top: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
`

const SingleChoiceFullListView = styled.View`
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
`

const SUPPORTED_LINKS = ['addFamilyMember']
const PLACEHOLDER_DEPENDENT_ID = 'dependent'

const IntakeSingleChoice = ({
  choices: choicesProp,
  choice,
  sendAnswer,
  disabled,
  member_app_fullscreen,
  choices_display_variant,
  isMultiPrompt = false,
  highlighted_choice_id = '',
}) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { currentChannelId } = useSelector(selectHistoryState)
  const [choices, setChoices] = useState(choicesProp)
  const [isAddFamilyMemberVisible, setIsAddFamilyMemberVisible] = useState(false)

  const isFullList = choices_display_variant === 'full_list'

  const highlightedOptionIndex = choices?.findIndex(
    (choice: Choice) => choice.id === highlighted_choice_id
  )

  const hasHighlightedOption =
    highlighted_choice_id !== '' && isFullList && highlightedOptionIndex !== -1

  const highlightedOption = hasHighlightedOption ? choices[highlightedOptionIndex] : null

  const ref = useScrollToTop([choices])
  const {
    layout: { height: containerHeight },
    onLayout: onScrollLayout,
  } = useOnLayout()
  const [yOffset, setYOffset] = useState(0)
  const [choiceSelectedIndex, setChoiceSelectedIndex] = useState<number | undefined>()
  const [dropdownValue, setDropdownValue] = useState<string>(
    choice?.props?.selected_choice?.id ?? ''
  )
  const [contentHeight, setContentHeight] = useState()
  const [overflowing, setOverflowing] = useState(false)
  const onContentSizeChange = useCallback(
    (_width, height) => setContentHeight(height),
    [setContentHeight]
  )
  const nearTop = yOffset <= 20
  const nearBottom = yOffset >= contentHeight - containerHeight - 40
  useEffect(() => {
    // schedule a cancelable update
    // 'cause state values don't get updated at the same time
    // in one render cycle
    // Smelly
    const id = setTimeout(() => {
      if (contentHeight && containerHeight) {
        setOverflowing(
          // Smelly
          isMobile() ? contentHeight > containerHeight : contentHeight >= containerHeight
        )
      }
    }, 0)
    return () => clearTimeout(id)
  }, [containerHeight, contentHeight, setOverflowing])

  useEffect(() => {
    const choiceId = choice?.props?.selected_choice?.id
    if (choiceId && choiceSelectedIndex === undefined) {
      const index = choices.findIndex((el) => el.id === choiceId)
      setChoiceSelectedIndex(index)
    }
  }, [choice, choices, choiceSelectedIndex])

  useEffect(() => {
    setDropdownValue(choice?.props?.selected_choice?.id ?? '')
  }, [choice])

  /**
   * Creates a new child option from the template.
   *
   * Example template:
   *{
   *   "display":"+ Add a new family member",
   *   "id":"dependent",
   *   "link":"addFamilyMember",
   *   "payload":{
   *      "slots":{
   *         "episode_subject":"dependent"
   *      }
   *   },
   *}
   *
   * Example return:
   *{
   *   "display":"Firstname Lastname (2020-02-02)",
   *   "id":"16875796",
   *   "payload":{
   *      "slots":{
   *         "episode_subject":"16875796"
   *      }
   *   }
   *}
   * @returns Choice a new child option
   */
  const createNewChildChoice = ({
    choiceTemplate,
    id,
    givenName,
    familyName,
    birthdate,
  }: {
    choiceTemplate: Choice
    id: number
    givenName: string
    familyName: string
    birthdate: string
  }): Choice => {
    const newChoice = JSON.parse(
      JSON.stringify(choiceTemplate).replaceAll(PLACEHOLDER_DEPENDENT_ID, id.toString())
    ) as Choice // replace all occurrence of placeholder with actual id
    newChoice.display = `${givenName} ${familyName} (${birthdate})`
    delete newChoice.link // remove the link for the new child option
    return newChoice
  }

  const createNewChoices = (choices: Choice[], newChoice: Choice): Choice[] => {
    const allButLast = choices.slice(0, choices.length - 1)
    const lastChoice = choices[choices.length - 1] // last one is the option link to add family member in MA
    return [...allButLast, newChoice, lastChoice] // insert the new option before the option link
  }
  const handleAdultAdded = useCallback(
    (_id: number) => {
      setIsAddFamilyMemberVisible(false)
      dispatch(PatientHistoryActions.archiveEpisodeRequest(currentChannelId))
    },
    [currentChannelId, dispatch]
  )

  const handleChildAdded = useCallback(
    (id: number, givenName: string, familyName: string, birthdate: string) => {
      setIsAddFamilyMemberVisible(false)
      const newChoice = createNewChildChoice({
        choiceTemplate: choices[choices.length - 1],
        id,
        givenName,
        familyName,
        birthdate,
      })
      const newChoices = createNewChoices(choices, newChoice)

      setChoices(newChoices)
      sendAnswer(newChoice)
    },
    [choices, sendAnswer]
  )

  const navigateToLink = useCallback((link: string) => {
    if (link === SUPPORTED_LINKS[0]) {
      setIsAddFamilyMemberVisible(true)
    }
  }, [])

  const getOnPress = useCallback(
    (choice: Choice, index: number) => () => {
      Analytics.trackEvent('button_click', { button_value: 'Single choice selected' })

      if (choice?.link && SUPPORTED_LINKS.includes(choice.link)) {
        navigateToLink(choice.link)
        return
      }

      sendAnswer(choice)
      setChoiceSelectedIndex(index)
    },
    [navigateToLink, sendAnswer]
  )

  const getOnSelect = useCallback(
    (choice: Choice) => {
      Analytics.trackEvent('button_click', {
        button_value: 'Single choice selected from dropdown',
      })

      sendAnswer(choice)
    },
    [sendAnswer]
  )

  const styleOfSingleChoiceView = useMemo(() => {
    if (member_app_fullscreen?.layout_style === 'skip_button') {
      return [
        Styles.singleChoiceSkipButton,
        overflowing && Styles.singleChoiceSkipButtonOverflowing,
      ]
    } else if (choices_display_variant === 'horizontal') {
      return [Styles.singleChoiceHorizontal, overflowing && Styles.singleChoiceOverflowing]
    } else if (choices_display_variant === 'dropdown') {
      return [Styles.singleChoiceDropdown, overflowing && Styles.singleChoiceOverflowing]
    } else {
      return [Styles.singleChoice, overflowing && Styles.singleChoiceOverflowing]
    }
  }, [member_app_fullscreen, choices_display_variant, overflowing])

  const handleDropdownChange = useCallback(
    (value: string) => {
      setDropdownValue(value)

      const selected = choices.find((c: Choice) => c.id === value)
      if (!selected) return

      getOnSelect(selected)
    },
    [choices, getOnSelect]
  )

  const handleModalClose = useCallback(() => {
    setIsAddFamilyMemberVisible(false)
  }, [])

  const renderChoice = useCallback(() => {
    if (choices?.length === 1) {
      return (
        <Button
          title={choices[0].display}
          disabled={disabled}
          onPress={getOnPress(choices[0], 0)}
          widthVariant="full"
        />
      )
    }
    if (choices?.length === 2 && member_app_fullscreen?.layout_style === 'skip_button') {
      return (
        <View style={Styles.singleChoiceOptionSkipButton}>
          <Button
            title={choices[0].display}
            disabled={disabled}
            onPress={getOnPress(choices[0], 0)}
            widthVariant="full"
          />
          <SkipButton
            variant="secondary"
            title={choices[1].display}
            disabled={disabled}
            onPress={getOnPress(choices[1], 1)}
            widthVariant="full"
          />
        </View>
      )
    }
    if (choices_display_variant === 'horizontal') {
      return choices.map((choice, i, array) => (
        <View key={i} style={Styles.singleChoiceHorizontalContainer}>
          <TouchableHighlight
            underlayColor={Colors.multiSelectMsgBg}
            disabled={disabled}
            style={[
              Styles.singleChoiceHorizontalOption,
              overflowing && Styles.singleChoiceHorizontalOptionOverflowing,
              i === 0 && Styles.firstSingleChoiceHorizontalOption,
              i === array.length - 1 && Styles.lastSingleChoiceHorizontalOption,
              i === choiceSelectedIndex && Styles.singleChoiceOptionSelected,
            ]}
            onPress={getOnPress(choice, i)}
          >
            <Typography variant="bodyNormal" align="center">
              {choice.display}
            </Typography>
          </TouchableHighlight>
        </View>
      ))
    }
    if (choices_display_variant === 'dropdown') {
      const addFamilyMemberChoice = choices.find(
        (c: Choice) => c.link && SUPPORTED_LINKS.includes(c.link)
      )
      const regularChoices = choices.filter(
        (c: Choice) => !(c.link && SUPPORTED_LINKS.includes(c.link))
      )
      const options = regularChoices.map(({ id, display }) => ({
        key: id,
        label: display,
        value: id,
      }))

      const addItemLabel = addFamilyMemberChoice?.display
      const handleAddItemPress = addFamilyMemberChoice?.link
        ? () => navigateToLink(addFamilyMemberChoice.link as string)
        : undefined

      return (
        <View testID="DROPDOWN_CONTAINER">
          <DropDown
            value={dropdownValue}
            items={options}
            onValueChange={handleDropdownChange}
            placeholder={I18n.t('IntakeComponent.dropdownPlaceholder')}
            addItemLabel={addItemLabel}
            onAddItemPress={handleAddItemPress}
          />
        </View>
      )
    }
    if (isFullList) {
      const choicesToRender = choices?.filter(
        (_: Choice, index: number) => index !== highlightedOptionIndex
      )

      const listCardItems = choicesToRender.map((choice: Choice, i: number) => (
        <IconCard
          key={choice.id ?? `intake-single-choice-icon-card-${i}`}
          title={choice?.display}
          description={choice?.description}
          rightIcon={{
            src: 'chevron-right',
            color: 'text',
          }}
          onPress={getOnPress(choice, i)}
          testID={`INTAKE_SINGLE_CHOICE_ICON_CARD_${i}`}
          accessibilityLabel={
            choice.display
              ? (I18n.t('AccessibilityLabels.singleChoiceSelection', {
                  option: choice.display,
                }) as string)
              : undefined
          }
          accessibilityRole="button"
        />
      ))
      return (
        <ListCardItemsContainer testID="LIST_CARD_ITEMS_CONTAINER">
          {listCardItems}
        </ListCardItemsContainer>
      )
    }
    return choices.map((choice, i, array) => (
      <TouchableHighlight
        key={i}
        underlayColor={Colors.multiSelectMsgBg}
        disabled={disabled}
        style={[
          Styles.singleChoiceOption,
          overflowing && Styles.singleChoiceOptionOverflowing,
          i === 0 && Styles.firstSingleChoiceOption,
          i === array.length - 1 && Styles.lastSingleChoiceOption,
          i === choiceSelectedIndex && Styles.singleChoiceOptionSelected,
        ]}
        onPress={getOnPress(choice, i)}
      >
        <Typography variant="bodyNormal" align={isMultiPrompt ? 'center' : 'right'}>
          {choice.display}
        </Typography>
      </TouchableHighlight>
    ))
  }, [
    choices,
    disabled,
    overflowing,
    member_app_fullscreen,
    choiceSelectedIndex,
    choices_display_variant,
    isMultiPrompt,
    getOnPress,
    dropdownValue,
    isFullList,
    highlightedOptionIndex,
    handleDropdownChange,
    navigateToLink,
  ])

  const renderDescription = useCallback(() => {
    if (choices_display_variant === 'horizontal' && choices.some((choice) => choice.description)) {
      const numDescriptions = choices.filter((choice) => choice.description).length
      return choices.map((choice, i) => (
        <View
          key={i}
          style={[
            Styles.singleSubtextContainer,
            { flex: numDescriptions === 2 ? 1.5 : choice.description ? 3 : 1 },
          ]}
        >
          <Typography
            variant="bodySmall"
            color="secondary"
            style={Styles.horizontalOptionSubtext}
            textAlign="center"
          >
            {choice.description}
          </Typography>
        </View>
      ))
    }
    return null
  }, [
    choices,
    disabled,
    overflowing,
    member_app_fullscreen,
    getOnPress,
    choiceSelectedIndex,
    choices_display_variant,
  ])

  const Container = isFullList ? SingleChoiceFullListContainer : View
  const SingleChoiceView = isFullList ? SingleChoiceFullListView : View
  const existingBottomMargin =
    member_app_fullscreen?.layout_style === 'skip_button' || !choices_display_variant
      ? theme.metrics.baseMargin
      : 0
  const bottomInsetStyle =
    !isMultiPrompt && theme.insets.bottom > existingBottomMargin
      ? { marginBottom: theme.insets.bottom }
      : undefined

  const choiceContent = (
    <>
      <SingleChoiceView style={!isFullList && [styleOfSingleChoiceView, bottomInsetStyle ?? {}]}>
        {renderChoice()}
      </SingleChoiceView>
      {choices_display_variant === 'horizontal' && (
        <View style={Styles.horizontalSubtextContainer}>{renderDescription()}</View>
      )}
    </>
  )

  return (
    <Container
      style={[
        Styles.singleChoiceContainer,
        !isFullList && overflowing && Styles.singleChoiceContainerOverflowing,
      ]}
    >
      {!isFullList && overflowing && !nearTop && (
        <Icon
          name="arrow-up"
          color={Colors.overlay}
          size={25}
          pointerEvents="none"
          style={[Styles.scrollArrow, { top: 0 }]}
        />
      )}
      {!isFullList && overflowing && !nearBottom && (
        <Icon
          name="arrow-down"
          color={Colors.overlay}
          size={25}
          pointerEvents="none"
          style={[Styles.scrollArrow, { bottom: 0 }]}
        />
      )}
      {isMultiPrompt ? (
        <View testID="SINGLE_CHOICE_CONTENT" style={Styles.singleChoiceScrollView}>
          {choiceContent}
        </View>
      ) : (
        <ScrollView
          testID="SINGLE_CHOICE_SCROLLVIEW"
          ref={ref}
          onLayout={onScrollLayout}
          onScroll={isWeb() ? ({ nativeEvent }) => setYOffset(nativeEvent.contentOffset.y) : null}
          onScrollEndDrag={({ nativeEvent }) => setYOffset(nativeEvent.contentOffset.y)}
          onMomentumScrollEnd={({ nativeEvent }) => setYOffset(nativeEvent.contentOffset.y)}
          onContentSizeChange={onContentSizeChange}
          alwaysBounceVertical={false}
          scrollIndicatorInsets={{ right: 1, top: 10, bottom: 10 }}
          style={[Styles.singleChoiceScrollView]}
        >
          {choiceContent}
        </ScrollView>
      )}
      {hasHighlightedOption && (
        <StickyButtonContainer testID="STICKY_BUTTON_CONTAINER">
          <Button
            variant="secondary"
            title={highlightedOption?.display}
            onPress={getOnPress(highlightedOption, highlightedOptionIndex)}
            widthVariant="full"
          />
        </StickyButtonContainer>
      )}
      <AddFamilyMemberModal
        isVisible={isAddFamilyMemberVisible}
        onClose={handleModalClose}
        onAdultAdded={handleAdultAdded}
        onChildAdded={handleChildAdded}
      />
    </Container>
  )
}

export default IntakeSingleChoice
